import { useEffect, useState } from 'react'
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { CircleAlert, Coins, Plus, RefreshCw } from 'lucide-react'
import {
  getV1MeRetrieveQueryOptions,
  useV1MeRetrieve,
  useV1QuizAdminCapabilities,
  useV1QuizAdminPushQuizzesList,
} from '@/api/client'
import { V1QuizAdminPushQuizzesListState } from '@/api/model'
import type { AdminPushQuiz } from '@/api/model'
import { PageHeader } from '@/components/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DataTable, type Column, type StateOption } from '@/components/data-table'
import { ActivationTabs } from '@/components/activation/activation-tabs'
import { PushQuizStateBadge } from '@/components/push-quiz/push-quiz-state-badge'
import { PushQuizNotificationStatusBadge } from '@/components/push-quiz/push-quiz-notification-status-badge'
import { PushQuizReminderStatusBadge } from '@/components/push-quiz/push-quiz-reminder-status-badge'
import { formatPolishDateTime } from '@/lib/push-quiz-dates'
import { scopeLabel } from '@/lib/push-quiz-labels'
import { pushQuizScoringModeLabel } from '@/components/push-quiz/push-quiz-scoring-labels'
import { QuizCapabilityNotice } from '@/components/quiz/quiz-capability-notice'
import { canAuthorQuiz, quizCapabilitiesOrClosed } from '@/components/quiz/quiz-capabilities'
import { queryHasHttpError } from '@/lib/query-state'

export const Route = createFileRoute('/_panel/zadania/')({
  beforeLoad: async ({ context }) => {
    // Operator-only area (admin + superadmin): everyone else is bounced out
    // before any quiz data loads (FR-001/FR-002).
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role !== 'admin' && me.data.role !== 'superadmin') {
      throw redirect({ to: '/' })
    }
  },
  component: PushQuizListPage,
})

const PAGE_SIZE = 25
const ALL = 'all'

type StateFilter = typeof ALL | V1QuizAdminPushQuizzesListState

const stateOptions: StateOption<StateFilter>[] = [
  { value: ALL, label: 'Wszystkie' },
  { value: V1QuizAdminPushQuizzesListState.draft, label: 'Szkice' },
  { value: V1QuizAdminPushQuizzesListState.scheduled, label: 'Zaplanowane' },
  { value: V1QuizAdminPushQuizzesListState.published, label: 'Opublikowane' },
  { value: V1QuizAdminPushQuizzesListState.archived, label: 'Zarchiwizowane' },
  { value: V1QuizAdminPushQuizzesListState.cancelled, label: 'Anulowane' },
]

/**
 * The push-quiz list — the "Quizy operatorskie" tab of the activating-tasks area: a
 * server-paginated list with state pills, an exact-match module-label filter and
 * a name search. Since feature 016 this is the first tab of the `/zadania` type
 * hub (student projects, polls and shared activities are the remaining tabs).
 */
function PushQuizListPage() {
  const navigate = useNavigate()
  const { data: me } = useV1MeRetrieve()
  const user = me?.status === 200 ? me.data : undefined
  const canManagePricing = user?.role === 'superadmin' || user?.role === 'admin'
  const capabilitiesQuery = useV1QuizAdminCapabilities()
  const capabilities = quizCapabilitiesOrClosed(
    capabilitiesQuery.data?.status === 200 ? capabilitiesQuery.data.data : undefined,
  )
  const canAuthor = canAuthorQuiz(capabilities)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [state, setState] = useState<StateFilter>(ALL)
  // Module label is a free-text EXACT-match wire param (django-filter default) —
  // debounced like 003's concept-key input so we don't fire a request per keystroke.
  const [moduleInput, setModuleInput] = useState('')
  const [moduleLabel, setModuleLabel] = useState('')
  useEffect(() => {
    const t = setTimeout(() => {
      if (moduleInput !== moduleLabel) {
        setModuleLabel(moduleInput)
        setPage(1)
      }
    }, 300)
    return () => clearTimeout(t)
  }, [moduleInput, moduleLabel])

  // Server-side filtering, search and paging (FR-004/FR-005): every control maps
  // to a wire param; `search` is name icontains only.
  const query = useV1QuizAdminPushQuizzesList({
    page,
    page_size: PAGE_SIZE,
    search: search.trim() || undefined,
    state: state === ALL ? undefined : state,
    module_label: moduleLabel.trim() || undefined,
  })
  const data = query.data?.status === 200 ? query.data.data : undefined
  // `customFetch` never throws on HTTP errors — a non-200 status IS the failure
  // (ui-contract list States: error note + retry, never the empty message).
  const failed = query.isError || (query.data !== undefined && query.data.status !== 200)

  const columns: Column<AdminPushQuiz>[] = [
    {
      header: 'Nazwa',
      cell: (quiz) => <span className="font-medium">{quiz.name}</span>,
    },
    {
      header: 'Stan',
      // `effective_state`, not `state`: a quiz past its availability window
      // still stores `published` and used to list as such (feature 080, A12).
      cell: (quiz) => <PushQuizStateBadge state={quiz.effective_state} />,
    },
    {
      header: 'Moduł',
      cell: (quiz) => <span className="text-muted-foreground">{quiz.module_label || '—'}</span>,
    },
    {
      header: 'Punktacja',
      cell: (quiz) => (
        <span className={quiz.grants_points === false ? 'font-medium text-amber-700' : ''}>
          {pushQuizScoringModeLabel(quiz.grants_points ?? true)}
        </span>
      ),
    },
    {
      header: 'Pytania',
      cell: (quiz) => <span className="tabular-nums">{quiz.items.length}</span>,
    },
    {
      header: 'Zakres',
      cell: (quiz) => <span className="text-muted-foreground">{scopeLabel(quiz.scope_kind)}</span>,
    },
    {
      header: 'Publikacja',
      cell: (quiz) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {quiz.state === 'scheduled' && quiz.scheduled_at
            ? `plan: ${formatPolishDateTime(quiz.scheduled_at)}`
            : quiz.published_at
              ? formatPolishDateTime(quiz.published_at)
              : '—'}
        </span>
      ),
    },
    {
      header: 'Powiadomienia',
      cell: (quiz) => (
        <span className="flex items-center gap-2">
          {quiz.state === 'draft' || quiz.state === 'scheduled' || !quiz.state ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <>
              <PushQuizNotificationStatusBadge status={quiz.notification_status} />
              <span className="whitespace-nowrap tabular-nums text-muted-foreground">
                {quiz.notified_count ?? 0} prób
              </span>
            </>
          )}
        </span>
      ),
    },
    {
      header: 'Przypomnienie',
      cell: (quiz) =>
        quiz.reminder_at ? (
          <span className="flex items-center gap-2 whitespace-nowrap text-muted-foreground">
            <span>{formatPolishDateTime(quiz.reminder_at)}</span>
            <PushQuizReminderStatusBadge status={quiz.reminder_status} />
          </span>
        ) : (
          <span className="text-muted-foreground">Brak</span>
        ),
    },
    {
      header: 'Dostępność',
      cell: (quiz) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {quiz.available_until
            ? `${new Date(quiz.available_until) <= new Date() ? 'Zakończona' : 'do'} ${formatPolishDateTime(quiz.available_until)}`
            : 'Bez terminu'}
        </span>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Zadania aktywizujące"
        actions={
          <span className="flex gap-2">
            {canManagePricing && (
              <Button asChild variant="outline" size="sm">
                <Link to="/punktacja">
                  <Coins data-icon="inline-start" /> Punktacja i nagrody
                </Link>
              </Button>
            )}
            <Button
              size="sm"
              disabled={!canAuthor}
              title={!canAuthor ? 'Autorowanie quizów jest wyłączone na serwerze.' : undefined}
              onClick={() => navigate({ to: '/zadania/nowe' })}
            >
              <Plus /> Nowy quiz operatorski
            </Button>
          </span>
        }
      />
      <ActivationTabs active="quizy" />
      <div className="flex flex-col gap-4 p-6">
        <QuizCapabilityNotice capabilities={capabilities} />
        {failed ? (
          <div className="flex flex-col items-center gap-3 rounded-xl border bg-background py-14 text-center">
            <CircleAlert className="size-6 text-destructive" />
            <p className="text-sm text-muted-foreground">
              Nie udało się pobrać listy quizów. Spróbuj ponownie.
            </p>
            <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
              <RefreshCw /> Spróbuj ponownie
            </Button>
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={[...(data?.results ?? [])]}
            rowKey={(quiz) => quiz.uid}
            count={data?.count ?? 0}
            page={page}
            onPageChange={setPage}
            pageSize={PAGE_SIZE}
            search={search}
            onSearchChange={(v) => {
              setSearch(v)
              setPage(1)
            }}
            searchPlaceholder="Szukaj po nazwie…"
            stateValue={state}
            stateOptions={stateOptions}
            onStateChange={(v) => {
              setState(v)
              setPage(1)
            }}
            filters={
              <Input
                value={moduleInput}
                onChange={(e) => setModuleInput(e.target.value)}
                placeholder="Etykieta modułu"
                className="h-8 w-44"
              />
            }
            isLoading={query.isLoading}
            isError={queryHasHttpError(query)}
            onRetry={() => void query.refetch()}
            emptyMessage="Brak quizów spełniających kryteria."
            onRowClick={(quiz) => navigate({ to: '/zadania/$uid', params: { uid: quiz.uid } })}
            countNoun={['quiz', 'quizów']}
          />
        )}
      </div>
    </>
  )
}
